import { useLayoutEffect, useCallback, useRef, RefObject } from 'react';

const HEADER_SELECTOR_DEFAULT = ':first-child';
const CONTENT_SELECTOR_DEFAULT = '#scrollable-content';
const FOOTER_SELECTOR_DEFAULT = 'footer';
const INDENT_ADJUSTMENT_DEFAULT = 2;

interface UseStickySidebarHeightOptions {
  scrollContainerId: string;
  drawerPaperRef: RefObject<HTMLElement | null>;
  headerSelector?: string;
  contentSelector?: string;
  footerSelector?: string;
  indentAdjustment?: number;
}

const getFooterVisibleHeight = (footer: Element): number => {
  const footerRect = footer.getBoundingClientRect();
  const viewportBottom = window.innerHeight;
  const footerVisible = viewportBottom - footerRect.top;

  return footerVisible > 0 ? Math.min(footerVisible, footerRect.height) : 0;
};

const getHeaderBottom = (header: Element): number =>
  header.getBoundingClientRect().bottom;

export const useStickySidebarHeight = ({
  scrollContainerId,
  drawerPaperRef,
  headerSelector = HEADER_SELECTOR_DEFAULT,
  contentSelector = CONTENT_SELECTOR_DEFAULT,
  footerSelector = FOOTER_SELECTOR_DEFAULT,
  indentAdjustment = INDENT_ADJUSTMENT_DEFAULT,
}: UseStickySidebarHeightOptions) => {
  const rafIdRef = useRef(0);

  const updatePosition = useCallback(() => {
    const paper = drawerPaperRef.current;
    if (!paper) return;

    const scrollContainer = document.getElementById(scrollContainerId);
    if (!scrollContainer) return;

    const header = scrollContainer.querySelector(headerSelector);
    if (!header) return;

    const footer =
      scrollContainer.querySelector(footerSelector) ||
      scrollContainer.lastElementChild;

    const headerBottom = getHeaderBottom(header);
    const visibleFooterHeight = footer ? getFooterVisibleHeight(footer) : 0;

    paper.style.top = `${headerBottom - 1}px`;
    paper.style.height = `calc(100% - ${headerBottom + Math.floor(visibleFooterHeight) - indentAdjustment}px)`;
  }, [
    scrollContainerId,
    drawerPaperRef,
    headerSelector,
    footerSelector,
    indentAdjustment,
  ]);

  useLayoutEffect(() => {
    const scrollContainer = document.getElementById(scrollContainerId);
    if (!scrollContainer) return;

    const header = scrollContainer.querySelector(headerSelector);

    if (!header) return;

    const content = scrollContainer.querySelector(contentSelector);

    updatePosition();

    const onScroll = () => {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(updatePosition);
    };

    const resizeObserver = new ResizeObserver(() => {
      updatePosition();
    });

    resizeObserver.observe(header);
    if (content) {
      resizeObserver.observe(content);
    }

    scrollContainer.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(rafIdRef.current);
      scrollContainer.removeEventListener('scroll', onScroll);
      resizeObserver.disconnect();
    };
  }, [scrollContainerId, headerSelector, contentSelector, updatePosition]);
};
